import React, { Component } from 'react'
import { findDOMNode } from 'react-dom'

import bookTickets from '../api/book-tickets'
import payBooking from '../api/pay-booking'
import { getResponseJson } from '../api/utils'
import getPerformance, {
    PERFORMANCE_PASSED_STATUS,
    PERFORMANCE_DISABLED_STATUS
} from '../api/get-performance'
import getPerformanceTicketSets from '../api/get-ticket-sets'

import { extractErrors } from '../utils/ErrorHandling'
import AdaptiveBlock from './utils/AdaptiveBlock'
import Pixel, {callPixel, PIXEL_ACTIONS} from './utils/Pixel'
import { ChosenTickets } from './selling-form/ChosenTickets'
import Loader from './utils/Loader'
import { ErrorMessage } from './utils/ErrorMessages'

import OutOfTickets from './messages/OutOfTickets'
import EventPassed from './messages/EventPassed'
import SalesStopped from './messages/SalesStopped'

import { TicketSelectorBlock, TicketSelectorPopup } from './selling-form/selector/TicketSelectorBlock'
import CustomerInfoPopup from './selling-form/CustomerInfoPopup'
import CheckOrderPopup from './selling-form/CheckOrderPopup'
import { 
    PublicOfferPopup, 
    RulesPopup,
    SuccessfulBookingPopup,
    RedirectionToBankPopup,
    BookingInProgressPopup,
} from './selling-form/messagePopups'

import NotFoundPage from '../pages/NotFoundPage'


class TicketsSeller extends Component {
    constructor (props) {
        super (props)
        this.state = {
            performance: null,
            tickets: null,
            notFound: false
        }
    }

    componentDidMount () {
        this.loadPerformance(this.props.performanceId)
        this.loadPerformanceTickets(this.props.performanceId)
    }

    render () {
        if (this.state.notFound) {
            return <NotFoundPage />
        }
        if (!this.state.performance || !this.state.tickets) {
            return <Loader /> 
        }

        if (this.state.performance.status === PERFORMANCE_PASSED_STATUS) {
            return <EventPassed />
        }
        if (this.state.performance.status === PERFORMANCE_DISABLED_STATUS) {
            return <SalesStopped message={this.state.performance.status_message} />
        }
        if (this.state.tickets.length === 0) {
            return <OutOfTickets />
        }

        return <SellingForm
            onMobile={this.props.onMobile ? this.props.onMobile : "button"}
            onDesktop={this.props.onDesktop ? this.props.onDesktop : "selector"}
            mobile={this.props.mobile}
            performance={this.state.performance}
            tickets={this.state.tickets} />
    }

    loadPerformance = (performanceId) => {
        getPerformance(performanceId)
            .then(response => {
                if (!response.ok) {
                    throw response
                }
                return response.json()
            }).then(result => {
                this.setState({
                    performance: result
                });
            }).catch(() => {
                this.setNotFound()
            })
    }

    loadPerformanceTickets = (performanceId) => {
        getPerformanceTicketSets(performanceId)
            .then(response => {
                if (!response.ok) {
                    throw response
                }
                return response.json()
            }).then(result => {
                this.setState({ tickets: result })
            }).catch(() => {
                this.setNotFound()
            })
    }

    setNotFound = () => {
        this.setState({
            notFound: true
        })
    }

}

class SellingForm extends Component {

    STEPS = {
        fresh: 'fresh',
        customerInfo: 'customerInfo',
        selectorPopup: 'selectorPopup',
        checkOrder: 'checkOrder',
        successfulBooking: 'successfulBooking',
        publicOffer: 'publicOffer',
        rules: 'rules',
        redirectionToBank: 'redirectionToBank',
        bookingInProgress: 'bookingInProgress',
    }

    constructor(props) {
        super(props)
        this.eventId = props.performance.event.uuid
        this.performanceId = props.performance.uuid
        this.prevStep = this.STEPS.fresh
    
        this.state = {
            errors: {},
            step: this.STEPS.fresh,
            chosenTickets: new ChosenTickets([]),
            email: '',
            name: '',
            phone: '',
            agreed: false,
            requestSent: false,
            paymentLink: false,
        }
    }

    componentDidMount() {
        window.ticketSeller = {
            openTickerSelector: () => this.goToStep(this.STEPS.selectorPopup),
            closeAllPopups: () => this.goToStep(this.STEPS.fresh),
            scrollToWidget: this.scrollToWidget,
        }
    }

    render() {
        return (
            <AdaptiveBlock
                    mobile={this.props.mobile}
                    tagname="form" 
                    onSubmit={this.disableSubmits} 
                    className="tickets-seller-form">
                
                <Pixel 
                    eventId={this.eventId} 
                    performanceId={this.performanceId} 
                    action={PIXEL_ACTIONS.VIEW} />

                <div hidden={this.props.onDesktop !== "selector"}
                        className="desktop-only">
                    <TicketSelectorBlock
                        selectorType={this.getSelectorType()}
                        tickets={this.props.tickets}
                        performance={this.props.performance}
                        chosenTickets={this.state.chosenTickets}
                        next={() => this.goToStep(this.STEPS.customerInfo)}
                        onUpdate={this.updateData} />
                </div>

                <button 
                    hidden={this.props.onMobile !== "button"}
                    className="tickets-button-primary mobile-only"
                    onClick={() => this.goToStep(this.STEPS.selectorPopup)}>
                    Купить билет
                </button>

                <TicketSelectorPopup
                    selectorType={this.getSelectorType()}
                    enabled={this.state.step === this.STEPS.selectorPopup}
                    tickets={this.props.tickets}
                    performance={this.props.performance}
                    chosenTickets={this.state.chosenTickets}
                    next={() => this.goToStep(this.STEPS.customerInfo)}
                    onClose={() => this.goToStep(this.STEPS.fresh)}
                    onUpdate={this.updateData} />

                <CustomerInfoPopup 
                    enabled={this.state.step === this.STEPS.customerInfo}
                    eventId={this.eventId} 
                    errors={this.state.errors}
                    performanceId={this.performanceId} 
                    policyAgreed={this.state.agreed}
                    next={() => this.goToStep(this.STEPS.checkOrder)}
                    back={() => this.goToStep(this.STEPS.selectorPopup)}
                    onClose={() => this.goToStep(this.STEPS.fresh)}
                    showOffer={() => this.goToStep(this.STEPS.publicOffer)}
                    showRules={() => this.goToStep(this.STEPS.rules)}
                    onUpdate={this.updateData} />

                <CheckOrderPopup
                    enabled={this.state.step === this.STEPS.checkOrder}
                    buttonDisabled={this.state.requestSent}
                    name={this.state.name}
                    email={this.state.email}
                    phone={this.state.phone}
                    errors={this.state.errors}
                    performance={this.props.performance}
                    chosenTickets={this.state.chosenTickets}
                    next={() => this.sendOrder()}
                    back={() => this.goToStep(this.STEPS.customerInfo)}
                    onClose={() => this.goToStep(this.STEPS.fresh)}
                    onUpdate={this.updateData} />

                <PublicOfferPopup
                    enabled={this.state.step === this.STEPS.publicOffer}
                    onClose={this.goBack} />

                <RulesPopup
                    enabled={this.state.step === this.STEPS.rules}
                    onClose={this.goBack} />

                <SuccessfulBookingPopup
                    enabled={this.state.step === this.STEPS.successfulBooking}
                    onClose={() => this.goToStep(this.STEPS.fresh)} />

                <BookingInProgressPopup
                    enabled={this.state.step === this.STEPS.bookingInProgress}
                    onClose={() => this.goToStep(this.STEPS.fresh)} />

                <RedirectionToBankPopup
                    enabled={this.state.step === this.STEPS.redirectionToBank}
                    onClose={() => this.goToStep(this.STEPS.fresh)}
                    paymentLink={this.state.paymentLink} />

                <ErrorMessage 
                    for='global'
                    errors={this.state.errors} />
            </AdaptiveBlock>
        )
    }

    disableSubmits = (event) => event.preventDefault()
    resetErrors = () => this.setState({errors: {}})
    scrollToWidget = () => findDOMNode(this).scrollIntoView({
        block: "center", 
        inline: "nearest",
        behavior: "smooth"
    })

    updateData = (newValues) => {
        this.resetErrors()
        this.setState(newValues)
    }

    goToStep = (newStep) => {
        if (!newStep) throw new Error(`Wrong step '${newStep}' in SellingForm!`)
        if (newStep !== this.prevStep) {
            this.prevStep = this.state.step
        }
        this.setState({step: newStep})
    }

    goBack = () => this.goToStep(this.prevStep)

    sendOrder = () => {
        callPixel({action: PIXEL_ACTIONS.WIDGET_PURCHASE_ATTEMPT})
        this.setState({ requestSent: true })
        let order_uuid = null
        this.goToStep(this.STEPS.bookingInProgress)
        bookTickets(
            this.performanceId,
            this.state.chosenTickets.getOrderSequence(),
            this.state.name,
            this.state.email,
            this.preparePhone(this.state.phone),
        )
        .then(getResponseJson)
        .then(result => {
            order_uuid = result.order_uuid
            this.goToStep(this.STEPS.successfulBooking)
            return payBooking(order_uuid)
        })
        .then(getResponseJson)
        .then(result => {
            if (result.success) {
                this.setState({paymentLink: result.redirectUrl})
                this.goToStep(this.STEPS.redirectionToBank)
                this.redirectToPaymentPage(result.redirectUrl)
            } else {
                throw result;
            }
        })
        .catch(error => {
            this.setState({ requestSent: false })
            if (error.json) {
                error.json().then(result => {
                    const errors = extractErrors(error, result)
                    this.showErrors(errors)
                });
            } else {
                this.showErrors({global: `Непредвиденная ошибка.`})
            }
        });
    }

    redirectToPaymentPage = (redirectUrl) => {
        this.setState({chosenTickets: new ChosenTickets([])})

        // redirection with history record example:
        //window.history.pushState({}, 'Заказ', `/order/${order_uuid}`)
        //window.open(redirectUrl, '_blank') 

        window.location.assign(redirectUrl)
    }

    showErrors = (errors) => {
        this.setState({errors: errors});

        if (
            errors.hasOwnProperty('email')
            || errors.hasOwnProperty('name')
            || errors.hasOwnProperty('phone')
        ) {
            this.goToStep(this.STEPS.customerInfo)
        }
        else if (errors.hasOwnProperty('global')) {
            this.goToStep(this.STEPS.fresh)
        }
    }

    getSelectorType = () => {
        if(this.props.performance && this.props.performance.seatmap_event_id)
            return 'seatmap'
        return 'list'
    }

    preparePhone = (phone) => {
        var filtered_phone = phone.replace(/[^\d^\\+]/gi, '')
        return filtered_phone.replace(/^8/, '+7')
    }
}

export default TicketsSeller
